import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ClientMessageData } from '@shared/api';
import {
  useAddManagerDeltaTimeDialogAddManagerDeltaTimePostMutation,
  useGetLastManagerMessageDialogGetLastManagerMessagePostMutation,
} from '@shared/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface UseManagerTimerParams {
  clientId: number;
  messages?: ClientMessageData[] | null;
}

const DEFAULT_DURATION_SEC = 180;
const MAX_DURATION_SEC = 900;
const DEFAULT_FORMATTED = '03:00';

const getUtcMs = (dateStr?: string | null) => {
  if (!dateStr) return NaN;

  const hasTz = /Z|[+-]\d{2}:?\d{2}$/i.test(dateStr);
  const normalizedDate = hasTz ? dateStr : `${dateStr}Z`;

  const ms = dayjs.utc(normalizedDate).valueOf();
  return Number.isNaN(ms) ? NaN : ms;
};

export const useManagerTimer = ({ clientId, messages }: UseManagerTimerParams) => {
  const [lastManagerMessage, setLastManagerMessage] = useState<ClientMessageData | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [timerDisabled, setTimerDisabled] = useState(false);
  const [lastMessageFetched, setLastMessageFetched] = useState(false);

  const [fetchLastManagerMessage] =
    useGetLastManagerMessageDialogGetLastManagerMessagePostMutation();
  const [addManagerDeltaMutation] = useAddManagerDeltaTimeDialogAddManagerDeltaTimePostMutation();

  useEffect(() => {
    setLastManagerMessage(null);
    setRemainingSeconds(0);
    setTimerDisabled(false);
    setLastMessageFetched(false);
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;

    fetchLastManagerMessage({
      bodyGetLastManagerMessageDialogGetLastManagerMessagePost: { client_id: clientId },
    })
      .then((result) => {
        if ('data' in result && result.data?.id) {
          setLastManagerMessage(result.data);
          setTimerDisabled(false);
          setLastMessageFetched(true);
          return;
        }

        if ('error' in result && (result.error as any)?.status === 404) {
          setTimerDisabled(true);
          setLastManagerMessage(null);
          setRemainingSeconds(0);
          setLastMessageFetched(true);
          return;
        }

        if ('error' in result) {
          setTimerDisabled(true);
          setLastManagerMessage(null);
          setRemainingSeconds(0);
          setLastMessageFetched(true);
        }
      })
      .catch(() => {});
  }, [clientId, fetchLastManagerMessage]);

  useEffect(() => {
    if (!lastMessageFetched) return;
    if (!messages?.length) return;

    const latestManagerMessage = [...messages]
      .filter((msg) => msg.creator_type === 'MANAGER')
      .sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))
      .at(-1);

    if (!latestManagerMessage) return;

    if (timerDisabled) {
      setTimerDisabled(false);
    }

    setLastManagerMessage((prev) => {
      if (!prev) return latestManagerMessage;

      if (prev.id === latestManagerMessage.id) {
        return {
          ...prev,
          ...latestManagerMessage,
        };
      }

      const prevMs = getUtcMs(prev.created_at);
      const nextMs = getUtcMs(latestManagerMessage.created_at);

      if (Number.isNaN(nextMs)) return prev;
      if (Number.isNaN(prevMs)) return latestManagerMessage;

      return nextMs > prevMs ? latestManagerMessage : prev;
    });
  }, [messages, timerDisabled, lastMessageFetched]);

  useEffect(() => {
    if (timerDisabled) {
      setRemainingSeconds(0);
      return;
    }

    if (!lastMessageFetched) {
      setRemainingSeconds(0);
      return;
    }

    if (!lastManagerMessage?.created_at) {
      setRemainingSeconds(0);
      return;
    }

    const createdAtMs = getUtcMs(lastManagerMessage.created_at);
    if (Number.isNaN(createdAtMs)) {
      setRemainingSeconds(0);
      return;
    }

    const deltaSeconds = Number(lastManagerMessage.manager_delta ?? 0);
    const totalDuration = DEFAULT_DURATION_SEC + (Number.isNaN(deltaSeconds) ? 0 : deltaSeconds);

    const effectiveDuration = Math.max(totalDuration, 0);

    const endTime = createdAtMs + effectiveDuration * 1000;

    const update = () => {
      const nowUtcMs = dayjs.utc().valueOf();
      const diff = Math.max(0, Math.floor((endTime - nowUtcMs) / 1000));
      setRemainingSeconds(diff);
    };

    update();
    const interval = setInterval(update, 1000);

    return () => clearInterval(interval);
  }, [lastManagerMessage?.created_at, lastManagerMessage?.manager_delta, timerDisabled]);

  const formatted = useMemo(() => {
    const minutes = String(Math.floor(remainingSeconds / 60)).padStart(2, '0');
    const seconds = String(remainingSeconds % 60).padStart(2, '0');

    return `${minutes}:${seconds}`;
  }, [remainingSeconds]);
  const hasManagerMessage = Boolean(lastManagerMessage?.id);

  const adjustDelta = useCallback(
    async (seconds: number) => {
      if (timerDisabled) return;
      if (!lastManagerMessage?.id) return;

      let increment = seconds;

      if (seconds > 0) {
        const maxAdd = MAX_DURATION_SEC - remainingSeconds;
        if (maxAdd <= 0) return;

        increment = Math.min(seconds, maxAdd);
      }

      try {
        const updated = await addManagerDeltaMutation({
          bodyAddManagerDeltaTimeDialogAddManagerDeltaTimePost: {
            message_id: lastManagerMessage.id,
            seconds: increment,
          },
        }).unwrap();

        if (updated?.id) {
          setLastManagerMessage(updated);
          return;
        }
      } catch {}

      setLastManagerMessage((prev) =>
        prev
          ? {
              ...prev,
              manager_delta: (Number(prev.manager_delta ?? 0) || 0) + increment,
            }
          : prev,
      );
    },
    [
      addManagerDeltaMutation,
      lastManagerMessage?.id,
      lastManagerMessage?.manager_delta,
      remainingSeconds,
      timerDisabled,
    ],
  );

  const addExtraTime = useCallback(() => {
    adjustDelta(30);
  }, [adjustDelta]);

  const resetTimer = useCallback(() => {
    adjustDelta(-1000);
  }, [adjustDelta]);

  const isActive = !timerDisabled && remainingSeconds > 0;

  const canAddMore = !timerDisabled && isActive && remainingSeconds < MAX_DURATION_SEC;

  return {
    remainingSeconds,
    formatted,
    defaultFormatted: DEFAULT_FORMATTED,
    hasManagerMessage,
    isActive,
    canAddMore,
    addExtraTime: timerDisabled ? null : addExtraTime,
    resetTimer: timerDisabled ? null : resetTimer,
  };
};
